import React from 'react';
import {Helmet} from 'react-helmet-async';
import {useLocation} from 'react-router-dom';
import {useLanguage} from '../contexts/LanguageContext';

const DocumentHead = () => {
  const {language} = useLanguage();
  const location = useLocation();

  const seoData = {
    en: {
      '/': {
        title: 'Online Metronome | Beat Master Precision Rhythm Tool',
        description: 'Beat Master is a free online metronome for setting tempo, practising rhythm, choosing time signatures and improving musical timing.',
        keywords: 'online metronome,metronome,music practice,rhythm tool,beat master,tempo trainer',
      },
      '/features': {
        title: 'What Features Does Beat Master Offer? | Online Metronome',
        description: 'Explore Beat Master features including precise tempo control, custom accents, subdivisions, multiple sounds and visual beat indicators.',
        keywords: 'metronome features,rhythm training,tempo control,beat accents,time signatures',
      },
      '/how-to-use': {
        title: 'How Do You Use an Online Metronome? | Beat Master Guide',
        description: 'Learn how to set tempo, select a time signature, use accents and subdivisions, and practise rhythm with the Beat Master metronome.',
        keywords: 'how to use metronome,metronome guide,rhythm practice,tempo practice',
      },
      '/about': {
        title: 'Who Is Behind Beat Master? | About Homy Tech Limited',
        description: 'Learn who created Beat Master, why the free online metronome was developed, and how to contact Homy Tech Limited.',
        keywords: 'about beat master,homy tech limited,online metronome developer,contact beat master',
      },
    },
    'zh-TW': {
      '/': {
        title: '線上拍子機｜Beat Master 專業節拍器',
        description: 'Beat Master 是免費線上拍子機，提供速度、拍號、節奏細分、重音及聲音設定，協助樂手改善節奏感與演奏時間。',
        keywords: '線上拍子機,拍子機,線上節拍器,節拍器,音樂練習,節奏訓練',
      },
      '/features': {
        title: 'Beat Master 有哪些功能？｜線上拍子機',
        description: '探索 Beat Master 的精準速度控制、自訂重音、節奏細分、多種聲音及視覺拍子提示等線上拍子機功能。',
        keywords: '拍子機功能,節拍器功能,節奏訓練,速度控制,拍號',
      },
      '/how-to-use': {
        title: '如何使用線上拍子機？｜Beat Master 使用指南',
        description: '學習如何設定速度、選擇拍號、使用重音和節奏細分，並透過 Beat Master 線上拍子機改善節奏感。',
        keywords: '如何使用拍子機,節拍器教學,節奏練習,速度練習',
      },
      '/about': {
        title: '誰開發了 Beat Master？｜關於 Homy Tech Limited',
        description: '了解 Beat Master 線上拍子機的開發團隊、產品理念，以及如何聯絡 Homy Tech Limited。',
        keywords: '關於 Beat Master,Homy Tech Limited,拍子機開發者,聯絡 Beat Master',
      },
    },
  };

  const currentLangData = seoData[language] || seoData.en;
  const currentSeo = currentLangData[location.pathname] || currentLangData['/'];
  const baseUrl = 'https://beatmaster.homy.hk';
  const canonicalUrl = `${baseUrl}${location.pathname}`;
  const socialImage = `${baseUrl}/social-cover.jpg`;

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${baseUrl}/#website`,
        name: 'Beat Master',
        alternateName: ['線上拍子機', 'Online Metronome'],
        url: `${baseUrl}/`,
        description: 'A free browser-based metronome for music practice, rhythm training and education.',
        inLanguage: ['en', 'zh-TW'],
        publisher: {'@id': `${baseUrl}/#organization`},
      },
      {
        '@type': 'Organization',
        '@id': `${baseUrl}/#organization`,
        name: 'Homy Tech Limited',
        url: 'https://homy.hk',
        description: 'Homy Tech Limited creates practical digital tools, including Beat Master.',
        sameAs: [
          'https://www.threads.com/@homy.hk',
          'https://www.instagram.com/homy.hk',
          'https://www.facebook.com/profile.php?id=61578745554661',
        ],
      },
    ],
  };

  return (
    <Helmet>
      <title>{currentSeo.title}</title>
      <meta name="description" content={currentSeo.description} />
      <meta name="keywords" content={currentSeo.keywords} />
      <meta name="robots" content="index,follow" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Beat Master" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={currentSeo.title} />
      <meta property="og:description" content={currentSeo.description} />
      <meta property="og:image" content={socialImage} />
      <meta property="og:image:alt" content="Beat Master precision online metronome" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={currentSeo.title} />
      <meta name="twitter:description" content={currentSeo.description} />
      <meta name="twitter:image" content={socialImage} />
      <link rel="canonical" href={canonicalUrl} />
      <link rel="manifest" href="/site.webmanifest" />
      <html lang={language === 'zh-TW' ? 'zh-TW' : 'en'} />
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
};

export default DocumentHead;